import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import './ChessLandingPage.css';
import logger from '../../utils/logger';
import { ChessGame } from './types';
import { useAuthContext } from '../../contexts/AuthContext';
import PlayerInfo from '../shared/PlayerInfo';

interface GamesMap {
    [gameId: string]: ChessGame;
}

type GameMode = 'vs-player' | 'vs-computer';

function ChessLandingPage() {
    const { isAuthenticated, user, signInWithTwitter, availableWallets, connectWallet } = useAuthContext();
    const [games, setGames] = useState<GamesMap>({});
    const [gameMode, setGameMode] = useState<GameMode>('vs-player');
    const [gameName, setGameName] = useState<string>('');
    const [playerName, setPlayerName] = useState<string>('');
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [showShareModal, setShowShareModal] = useState<boolean>(false);
    const [createdGameId, setCreatedGameId] = useState<string>('');
    const [linkCopied, setLinkCopied] = useState<boolean>(false);
    const [showSignInOptions, setShowSignInOptions] = useState<boolean>(false);
    const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
    const navigate = useNavigate();

    const navigateToHome = (): void => {
        navigate("/");
    };

    const navigateToGames = (): void => {
        navigate("/mferchess/games");
    };

    useEffect(() => {
        // Fetch the list of all games
        fetch(`${SERVER_URL}/api/chess/games`)
            .then((response) => response.json())
            .then((data: GamesMap) => {
                logger.debug("Fetched games", data);
                setGames(data);
            })
            .catch((error) => logger.error('Error fetching the games list:', error));
    }, [SERVER_URL]);

    // Filter joinable games (waiting for players)
    const joinableGames = useMemo(() => {
        return (Object.entries(games) as [string, ChessGame][])
            .filter(([, game]) => game.state === "waiting" && game.players.length < 2)
            .sort(([, gameA], [, gameB]) => gameB.lastActivity - gameA.lastActivity)
            .slice(0, 5); // Show top 5 joinable games
    }, [games]);

    const handleCreateGameClick = (): void => {
        setShowCreateModal(true);
    };

    const handleCreateModalNext = async (): Promise<void> => {
        // Validate player name if not signed in
        if (!isAuthenticated && !playerName.trim()) {
            alert('Please enter your name.');
            return;
        }

        // Use default name if game name is empty
        const finalGameName = gameName.trim() || 'Untitled Game';
        const isVsComputer = gameMode === 'vs-computer';

        // Get display name from auth or manual input
        const displayName = isAuthenticated
            ? (user?.displayName || user?.twitterHandle || 'Player')
            : playerName.trim();

        try {
            const response = await fetch("/api/chess/game", {
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    gameName: finalGameName,
                    autoplay: isVsComputer,
                    playerName: displayName,
                    userId: user?.id || null
                })
            });
            logger.debug("Create game response", { status: response.status });
            if (!response.ok) throw new Error("Failed to create game");
            const game: { gameId: string } = await response.json();

            // Close create modal
            setShowCreateModal(false);

            // If playing against computer, navigate directly to game
            if (isVsComputer) {
                navigate(`/mferchess/${game.gameId}`);
            } else {
                // Show share modal for multiplayer games
                setCreatedGameId(game.gameId);
                setLinkCopied(false);
                setShowShareModal(true);
            }
        } catch (error) {
            logger.error("Error creating game:", error);
        }
    };

    const joinGame = (uuid: string): void => {
        window.location.href = `/mferchess/${uuid}`;
    };

    const copyToClipboard = (text: string): void => {
        navigator.clipboard.writeText(text).then(() => {
            setLinkCopied(true);
        }).catch(err => {
            logger.error('Failed to copy to clipboard', err);
        });
    };

    const handleNextFromShareModal = (): void => {
        setShowShareModal(false);
        setLinkCopied(false);
        navigate(`/mferchess/${createdGameId}`);
    };

    return (
        <div className="game-container">
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <img
                    src="/images/chess-knight.png"
                    alt="Chess Knight"
                    style={{ width: '100px', height: 'auto', marginBottom: '10px' }}
                />
                <h1 className="title" style={{ margin: '0' }}>Mfer Chess</h1>
            </div>

            {/* Auth Status Display */}
            <div className="auth-status">
                {isAuthenticated ? (
                    <PlayerInfo />
                ) : (
                    <>
                        <span style={{ color: '#666' }}>Playing as guest</span>
                        <button
                            className="sign-in-btn"
                            onClick={() => setShowSignInOptions(!showSignInOptions)}
                        >
                            Sign in
                        </button>
                    </>
                )}
            </div>

            {/* Sign In Options Dropdown */}
            {!isAuthenticated && showSignInOptions && (
                <div className="sign-in-options">
                    <button className="sign-in-option" onClick={signInWithTwitter}>
                        <img src="/images/x-logo-black.png" alt="X" className="x-logo" />
                        Sign in with X
                    </button>
                    {availableWallets.filter(w => w.available).map(wallet => (
                        <button
                            key={wallet.id}
                            className="sign-in-option"
                            onClick={() => connectWallet(wallet.id)}
                        >
                            {wallet.name}
                        </button>
                    ))}
                </div>
            )}

            {/* Mode Selector */}
            <div className="mode-selector">
                <button
                    className={`mode-tab ${gameMode === 'vs-player' ? 'active' : ''}`}
                    onClick={() => setGameMode('vs-player')}
                >
                    vs Player
                </button>
                <button
                    className={`mode-tab ${gameMode === 'vs-computer' ? 'active' : ''}`}
                    onClick={() => setGameMode('vs-computer')}
                >
                    vs Computer
                </button>
            </div>

            {/* Quick Join Section - only show for vs-player mode */}
            {gameMode === 'vs-player' && joinableGames.length > 0 && (
                <div className="quick-join-section">
                    <h3 className="quick-join-title">Quick Join</h3>
                    <div className="quick-join-list">
                        {joinableGames.map(([uuid, game]) => (
                            <div key={uuid} className="quick-join-item">
                                <div className="game-info">
                                    <span className="game-name">{game.gameName}</span>
                                    <span className="game-status">Waiting for opponent</span>
                                </div>
                                <button className="join-btn" onClick={() => joinGame(uuid)}>
                                    Join
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button onClick={navigateToGames} className="view-games-button">
                    All Games
                </button>
                <button onClick={handleCreateGameClick} className="create-game-button">
                    {gameMode === 'vs-computer' ? 'Play vs Computer' : 'Create Game'}
                </button>
            </div>

            <section>
                <h3>Credits</h3>
                <p>
                    Game Design: Sartocrates
                </p>
                <p>
                    Dev: nngmi, anonchickenlegs
                </p>
                <p>
                    Artwork: mfers by sartoshi
                </p>
                <p>
                    Sounds: Pixabay
                </p>
            </section>
            <button onClick={navigateToHome} className="back-button">
                Back to Home
            </button>

            {/* Create Game Modal - Step 1 */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>{gameMode === 'vs-computer' ? 'Play vs Computer' : 'Create a New Game'}</h3>

                        <div style={{ display: 'table', width: '100%', marginBottom: '15px', marginTop: '20px' }}>
                            <div style={{ display: 'table-row' }}>
                                <div style={{ display: 'table-cell', padding: '8px', textAlign: 'left', verticalAlign: 'middle' }}>
                                    <label>Your Name:</label>
                                </div>
                                <div style={{ display: 'table-cell', padding: '8px' }}>
                                    {isAuthenticated ? (
                                        <PlayerInfo />
                                    ) : (
                                        <input
                                            type="text"
                                            placeholder="Enter Your Name"
                                            value={playerName}
                                            onChange={(e) => setPlayerName(e.target.value)}
                                            style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                            required
                                        />
                                    )}
                                </div>
                            </div>

                            <div style={{ display: 'table-row' }}>
                                <div style={{ display: 'table-cell', padding: '8px', textAlign: 'left', verticalAlign: 'middle' }}>
                                    <label>Game Name:</label>
                                </div>
                                <div style={{ display: 'table-cell', padding: '8px' }}>
                                    <input
                                        type="text"
                                        placeholder="Optional"
                                        value={gameName}
                                        onChange={(e) => setGameName(e.target.value)}
                                        style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleCreateModalNext}
                            disabled={!isAuthenticated && !playerName.trim()}
                            style={{
                                backgroundColor: (isAuthenticated || playerName.trim()) ? '#4CAF50' : '#ccc',
                                color: 'white',
                                padding: '12px 30px',
                                fontSize: '16px',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: (isAuthenticated || playerName.trim()) ? 'pointer' : 'not-allowed',
                                width: '100%',
                                opacity: (isAuthenticated || playerName.trim()) ? 1 : 0.6
                            }}
                        >
                            {gameMode === 'vs-computer' ? 'Start Game' : 'Next'}
                        </button>
                    </div>
                </div>
            )}

            {/* Share Game Modal - Step 2 */}
            {showShareModal && (
                <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Game Created!</h3>
                        <p style={{ marginBottom: '20px' }}>Share this game link with your friend:</p>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Game Link:</label>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    value={`${window.location.origin}/mferchess/${createdGameId}`}
                                    readOnly
                                    style={{ flex: 1, padding: '8px', fontSize: '14px' }}
                                />
                                <button
                                    onClick={() => copyToClipboard(`${window.location.origin}/mferchess/${createdGameId}`)}
                                    style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        backgroundColor: linkCopied ? '#4CAF50' : undefined,
                                        color: linkCopied ? 'white' : undefined,
                                        border: linkCopied ? 'none' : undefined
                                    }}
                                >
                                    {linkCopied ? '✓' : '📋 Copy'}
                                </button>
                            </div>
                        </div>

                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                            Or have them go to the mfers arcade chess room
                        </p>

                        <button
                            onClick={handleNextFromShareModal}
                            disabled={!linkCopied}
                            style={{
                                backgroundColor: linkCopied ? '#4CAF50' : '#ccc',
                                color: 'white',
                                padding: '12px 30px',
                                fontSize: '16px',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: linkCopied ? 'pointer' : 'not-allowed',
                                width: '100%',
                                opacity: linkCopied ? 1 : 0.6
                            }}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ChessLandingPage;
